/**
 * How old are the sources in a reference list?
 *
 * Reads the publication year of every entry in a pasted list — in any style — and
 * measures it against a window ("sources from the last five years"). No style guide
 * sets such a window; instructors, programs and journals do, so the window is the
 * reader's to set and this only does the counting.
 *
 * The year is the hard part, because a reference holds many four-digit numbers that
 * are not its year. Before looking, the parser removes the ones it can recognise:
 * URLs and DOIs (`10.1109/TBME.2011.2158315`), access and retrieval dates ("Retrieved
 * May 4, 2024, from", "Accessed: Jan. 11, 2019", "[Accessed 4 May 2026]", NLM's
 * "[cited 2015 Oct 23]"), page ranges after "p."/"pp." or a locator colon, and
 * ISBNs. Then it prefers, in order:
 *
 * 1. a year before a semicolon — Vancouver and AMA: "2002 Jul 25;347(4)" (checked
 *    first, so a title such as "Trends in obesity (1990–2010)" cannot win);
 * 2. a year that opens a short parenthesis — APA, Harvard, Chicago author-date and
 *    Chicago's "Journal 12, no. 3 (2019)": "(2019)", "(2019a)", "(2019, March 5)",
 *    "(Spring 2019)";
 * 3. otherwise the last plausible year left — MLA, IEEE and bibliography styles put
 *    the year after the title and the publisher.
 *
 * "n.d." and "no date" mark an undated source; "in press", "forthcoming", "to be
 * published" and "submitted for publication" mark one that is not out yet.
 */

import { parseNumberedList } from "@/lib/tool-kit/reference-checkers/numbered-list";

export type YearStatus = "dated" | "undated" | "in-press" | "unknown";

export interface AgedReference {
  position: number;
  text: string;
  year: number | null;
  status: YearStatus;
  /** Years between the publication year and the reference year; null when undated. */
  age: number | null;
  /** Published before the cutoff year. */
  old: boolean;
}

export interface YearCount {
  year: number;
  count: number;
  old: boolean;
}

export interface AgeReport {
  references: AgedReference[];
  total: number;
  dated: number;
  undated: number;
  inPress: number;
  unknown: number;
  /** Dated or in press, and inside the window. */
  within: number;
  older: number;
  /** within ÷ (dated + in press), as a fraction; null when nothing is dated. */
  shareWithin: number | null;
  medianAge: number | null;
  newest: number | null;
  oldest: number | null;
  /** The earliest year inside the window: references before it are flagged. */
  cutoffYear: number;
  byYear: YearCount[];
}

/** The earliest year read as a publication year. */
const EARLIEST_YEAR = 1450;

const RE_URL = /\b(?:https?:\/\/|www\.)\S+/gi;
const RE_DOI = /\b10\.\d{4,9}\/\S+/g;
const RE_ISBN = /\bISBN(?:-1[03])?:?\s*[\dXx][\d\sXx-]{8,}/g;
const RE_ACCESS_CLAUSES = [
  // APA 6 / Harvard variants: "Retrieved May 4, 2024, from"
  /\bretrieved\b[^.]*?\d{4},?\s*(?:from\b)?/gi,
  // IEEE "Accessed: Jan. 11, 2019", AMA "Accessed March 5, 2024", MLA "Accessed 4 May 2024"
  /\baccessed:?\s+(?:on\s+)?(?:[A-Za-z]+\.?\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+[A-Za-z]+\.?,?\s+\d{4}|\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4})/gi,
  // Harvard "[Accessed 4 May 2026]", NLM "[cited 2015 Oct 23]", "(accessed …)"
  /[[(](?:accessed|cited|viewed|retrieved)\b[^\])]*[\])]/gi,
  /\bviewed\s+\d{1,2}\s+[A-Za-z]+\.?\s+\d{4}/gi,
];
/** "pp. 1563–1571", "p. 2019", ":1563-71" — page numbers in a year's range. */
const RE_PAGES = /(?:\bpp?\.\s*|:\s*)[A-Za-z]?\d+(?:\s*[-–—]\s*[A-Za-z]?\d+)?/g;
const RE_UNDATED = /\(n\.\s?d\.?\)|\bn\.\s?d\.|\bno date\b/i;
const RE_IN_PRESS =
  /\bin press\b|\bforthcoming\b|\bto be published\b|\bsubmitted for publication\b|\baccepted for publication\b/i;
/** "(2019)", "(2019a)", "(2019, March 5)", "(Spring 2019)" — not "(1990–2010)". */
const RE_PAREN_YEAR =
  /\((?:[A-Z][a-z]+\.?\s+)?(\d{4})[a-z]?(?:,\s*[^()]{1,30})?\)/g;
const RE_SEMICOLON_YEAR =
  /\b(\d{4})(?:\s+[A-Z][a-z]{2,8}\.?(?:\s+\d{1,2})?)?(?:\s+\[[^\]]*\])?\s*;/;
const RE_ANY_YEAR = /(?<![\d/.:-])(\d{4})(?:[a-z])?(?![\d])/g;

function clean(text: string): string {
  let out = text
    .replace(RE_URL, " ")
    .replace(RE_DOI, " ")
    .replace(RE_ISBN, " ");
  for (const re of RE_ACCESS_CLAUSES) {
    out = out.replace(re, " ");
  }
  return out.replace(RE_PAGES, " ");
}

function plausible(year: number, maxYear: number): boolean {
  return year >= EARLIEST_YEAR && year <= maxYear;
}

function parenthesizedYear(text: string, maxYear: number): number | null {
  for (const match of text.matchAll(RE_PAREN_YEAR)) {
    const year = Number(match[1]);
    if (plausible(year, maxYear)) {
      return year;
    }
  }
  return null;
}

function lastYear(text: string, maxYear: number): number | null {
  let found: number | null = null;
  for (const match of text.matchAll(RE_ANY_YEAR)) {
    const year = Number(match[1]);
    if (plausible(year, maxYear)) {
      found = year;
    }
  }
  return found;
}

/**
 * The publication year of one reference, or why there is none. `currentYear` bounds
 * what counts as a year: anything later than next year is a page or a report number.
 */
export function extractYear(
  entry: string,
  currentYear: number
): { year: number | null; status: YearStatus } {
  const maxYear = currentYear + 1;
  const text = clean(entry);
  if (RE_IN_PRESS.test(text)) {
    return { year: null, status: "in-press" };
  }
  if (RE_UNDATED.test(text)) {
    return { year: null, status: "undated" };
  }
  const semicolon = RE_SEMICOLON_YEAR.exec(text);
  const semicolonYear =
    semicolon && plausible(Number(semicolon[1]), maxYear)
      ? Number(semicolon[1])
      : null;
  const year =
    semicolonYear ??
    parenthesizedYear(text, maxYear) ??
    lastYear(text, maxYear);
  return year === null
    ? { year: null, status: "unknown" }
    : { year, status: "dated" };
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function countByYear(references: readonly AgedReference[]): YearCount[] {
  const counts = new Map<number, YearCount>();
  for (const reference of references) {
    if (reference.year === null) {
      continue;
    }
    const row = counts.get(reference.year) ?? {
      year: reference.year,
      count: 0,
      old: reference.old,
    };
    row.count++;
    counts.set(reference.year, row);
  }
  return [...counts.values()].sort((a, b) => b.year - a.year);
}

/**
 * Age every reference in a pasted list against a window of `windowYears` counted back
 * from `currentYear`: with 2026 and 5, references from 2021 on are inside the window
 * and anything published in 2020 or earlier is flagged.
 */
export function ageReport(
  text: string,
  currentYear: number,
  windowYears: number
): AgeReport {
  const cutoffYear = currentYear - windowYears;
  const references: AgedReference[] = parseNumberedList(text).map((entry) => {
    const { year, status } = extractYear(entry.text, currentYear);
    const age = year === null ? null : Math.max(0, currentYear - year);
    return {
      position: entry.position,
      text: entry.text,
      year,
      status,
      age,
      old: year !== null && year < cutoffYear,
    };
  });
  const count = (status: YearStatus) =>
    references.filter((reference) => reference.status === status).length;
  const dated = count("dated");
  const inPress = count("in-press");
  const older = references.filter((reference) => reference.old).length;
  const within = dated + inPress - older;
  const years = references
    .map((reference) => reference.year)
    .filter((year): year is number => year !== null);
  return {
    references,
    total: references.length,
    dated,
    undated: count("undated"),
    inPress,
    unknown: count("unknown"),
    within,
    older,
    shareWithin: dated + inPress > 0 ? within / (dated + inPress) : null,
    medianAge: median(
      references
        .map((reference) => reference.age)
        .filter((age): age is number => age !== null)
    ),
    newest: years.length > 0 ? Math.max(...years) : null,
    oldest: years.length > 0 ? Math.min(...years) : null,
    cutoffYear,
    byYear: countByYear(references),
  };
}
